import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  MUIDataTable,
  MUIDataTableColumn,
  MUIDataTableMeta,
  MUIDataTableOptions,
  TableActionTypes,
  TableState,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import { candidateService, GenericErrorResponse } from '../../../api';
import {
  GenerateCandidateRequest,
  RawCandidateFunctionRequest,
} from '../../../api/candidate/iexam-create/@opadmin/models';
import { iExamMasterCodeMappingToSelect, useAPIDropdown } from '../../../hooks/dropdown';
import { CustomMutateReturn } from '../../../hooks/query';
import { getIndexValue } from '../../../utils/ArrayUtils';
import { RetrieveS3URLResponse } from '../../../api/document/files/api-services/model';
import DownloadFromS3Action from '../../../components/buttons/downloadFromS3Action';
import {
  accountCreationColumn,
  actionColumn,
  authenticationColumn,
  candidateColumn,
  candidateTypeColumn,
  citizenshipColumn,
  emailColumn,
  emailSentColumn,
  examLevelColumn,
  indexNumberColumn,
  schoolColumn,
  sendEmailColumn,
  statutoryNameColumn,
} from './IExamCandidateCreateCloumn';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#iexam-candidate-create-table .MuiTableCell-root': {
      '&[data-colindex="1"]': {
        minWidth: 208,
      },
      '&[data-colindex="4"]': {
        minWidth: 160,
      },
      '&[data-colindex="6"]': {
        minWidth: 180,
      },
      '&[data-colindex="8"]': {
        minWidth: 229,
      },
      '&[data-colindex="9"]': {
        minWidth: 183,
      },
    },
  },
  select: {
    marginBottom: theme.spacing(3),
  },
  citizenText: {
    color: theme.color.text.textTertiary,
  },
  accountCreationText: {
     ...typeTheme(theme, 'body2bold'),
  },
  emailSentText: {
    minWidth: '150px',
  },
  iconContainer: {
    width: '100%',
    display: 'inline-flex',
  },
  viewDetailsButton: {
    color: theme.color.link.linkDefault01,
    whiteSpace: 'nowrap',
    padding: 0,
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
  },
  icon: {
    margin: 'auto',
    marginRight: '12px',
  },
  iconDefault: {
    color: theme.color.link.linkDefault01,
  },
  iconDisable: {
    color: theme.color.link.linkDisabledBg01,
  },
  iconEmail: {
    marginTop: '12px',
    marginLeft: '8px',
  },
}));

const useRawFilters = () => {
  const candidateType = useAPIDropdown(
    () => candidateService.iexamCreate.opadmin.useGetFilterListing('candidate-type', ''),
    true,
    iExamMasterCodeMappingToSelect
  );

  const school = useAPIDropdown(
    () => candidateService.iexamCreate.opadmin.useGetFilterListing('school', ''),
    true,
    iExamMasterCodeMappingToSelect
  );

  const examLevel = useAPIDropdown(
    () =>
      candidateService.iexamCreate.opadmin.useGetFilterListing(
        'exam-level',
        FUNCTION_NAMES.BULK_CANDIDATE_CREATION
      ),
    true,
    iExamMasterCodeMappingToSelect
  );

  const authentication = useAPIDropdown(
    () => candidateService.iexamCreate.opadmin.useGetFilterListing('authentication', ''),
    true,
    iExamMasterCodeMappingToSelect
  );

  const citizenship = useAPIDropdown(
    () => candidateService.iexamCreate.opadmin.useGetFilterListing('citizenship', ''),
    true,
    iExamMasterCodeMappingToSelect
  );

  return {
    candidateType,
    school,
    examLevel,
    authentication,
    citizenship,
  };
};

const useBulkActions = (
  state: TableState,
  localAuthentication: {
    [key: string]: any;
  },
  generateMutation: CustomMutateReturn<
    AxiosResponse<undefined>,
    AxiosError<GenericErrorResponse>,
    GenerateCandidateRequest[],
    unknown
  >,
  emailMutate: (
    type: 'ALL' | 'SELECTED' | 'INDIVIDUAL',
    input: RawCandidateFunctionRequest
  ) => void,
  emailLoading: boolean,
  exportAction: {
    onHandleExport: (input: RawCandidateFunctionRequest) => void;
    fileUUID: string;
    downloadCSV: (status: RetrieveS3URLResponse) => void;
    disableExportButton: boolean;
  },
  translations: {
    emailSelectedLabel: string;
    exportSelectedLabel: string;
  }
) => {
  const { mutate: generateMutate, isLoading: generateLoading } = generateMutation;

  const canBulkGenerate = useMemo(() => {
    if (Object.keys(state.rowsSelected).length === 0) {
      return false;
    }
    return Object.keys(state.rowsSelected).reduce(
      (accumulator, currentValue) =>
        accumulator && !state.rowsSelected[currentValue].accountDateTime,
      true
    );
  }, [state.rowsSelected]);
  const canBulkEmail = useMemo(() => {
    if (Object.keys(state.rowsSelected).length === 0) {
      return false;
    }
    return Object.keys(state.rowsSelected).reduce(
      (accumulator, currentValue) =>
        accumulator || state.rowsSelected[currentValue].accountDateTime,
      false
    );
  }, [state.rowsSelected]);

  return useMemo(
    () => [
      canBulkGenerate && (
        <Button
          disabled={generateLoading}
          variant="text"
          color="primary"
          onClick={() =>
            generateMutate(
              Object.keys(state.rowsSelected).map((s) => {
                const { uuid, candidateId, authentication } = state.rowsSelected[s];
                return {
                  uuid,
                  authentication: localAuthentication[candidateId] || authentication,
                } as GenerateCandidateRequest;
              })
            )
          }
        >
          Generate Account
        </Button>
      ),
      canBulkEmail && (
        <Button
          disabled={emailLoading}
          variant="text"
          color="primary"
          onClick={() => {
            const selectedList: string[] = Object.keys(state.rowsSelected).map(
              (s) => state.rowsSelected[s].uuid
            );
            emailMutate('SELECTED', {
              uuid: selectedList,
              candidateCreationSearchParamModel: {
                candidateType: getIndexValue(state.filters.candidateType, 0),
                candidateSchool: state.filters.school || [],
                examLevel: getIndexValue(state.filters.examLevel, 0),
                authenticationType: getIndexValue(state.filters.authentication, 0),
                citizenship: getIndexValue(state.filters.citizenship, 0),
                search: state.search ?? '',
              },
            });
          }}
        >
          {translations.emailSelectedLabel}
        </Button>
      ),
      <DownloadFromS3Action
        key="downloadButton"
        color="primary"
        actionType="link"
        requestRoute={{ data: exportAction.fileUUID, url: '/api/s3/files' }}
        disable={exportAction.disableExportButton}
        onClick={() => {
          const selectedList: string[] = Object.keys(state.rowsSelected).map(
            (s) => state.rowsSelected[s].uuid
          );
          exportAction.onHandleExport({
            uuid: selectedList,
            candidateCreationSearchParamModel: {
              candidateType: getIndexValue(state.filters.candidateType, 0),
              candidateSchool: state.filters.school || [],
              examLevel: getIndexValue(state.filters.examLevel, 0),
              authenticationType: getIndexValue(state.filters.authentication, 0),
              citizenship: getIndexValue(state.filters.citizenship, 0),
              search: state.search ?? '',
            },
          });
        }}
        onDownload={(status) => exportAction.downloadCSV(status)}
        label={translations.exportSelectedLabel}
      />,
    ],
    [
      canBulkEmail,
      canBulkGenerate,
      emailLoading,
      emailMutate,
      exportAction,
      generateLoading,
      generateMutate,
      localAuthentication,
      state.rowsSelected,
      state.filters.candidateType,
      state.filters.school,
      state.filters.examLevel,
      state.filters.authentication,
      state.filters.citizenship,
      state.search,
      translations.exportSelectedLabel,
      translations.emailSelectedLabel,
    ]
  );
};

interface UseTableColumnProps {
  remoteTotalRowsSelected: number;
  hasCreateAccess: boolean;
  hasEditAccess: boolean;
  tableStateMgmt: {
    state: TableState;
    tableDispatch: React.Dispatch<TableActionTypes>;
  };
  filters: {
    filterMapping: {
      filterMappingCitizenship: any;
      filterMappingCandidateType: any;
      filterMappingExamLevel: any;
      filterMappingSchool: any;
      filterMappingAuthentication: any;
    };
    filterCode: {
      filterCodeCitizenship: any;
      filterCodeCandidateType: any;
      filterCodeExamLevel: any;
      filterCodeSchool: any;
      filterCodeAuthentication: any;
    };
  };
  generateAccount: {
    generateLoading: boolean;
    generateMutate: (tableMeta: MUIDataTableMeta) => void;
  };
  email: {
    emailLoading: boolean;
    emailMutate: (tableMeta: MUIDataTableMeta) => void;
  };
  saveLocalAuthentication: (event: any, tmpMeta: MUIDataTableMeta) => void;
  editDetail: (editDetail: any[]) => void;
}

const useTableColumns = ({
  remoteTotalRowsSelected,
  hasCreateAccess,
  hasEditAccess,
  tableStateMgmt,
  filters,
  generateAccount,
  email,
  editDetail,
}: UseTableColumnProps): MUIDataTableColumn[] => {
  const { classes } = useStyles();
  const {
    filterMappingCitizenship,
    filterMappingCandidateType,
    filterMappingExamLevel,
    filterMappingSchool,
    filterMappingAuthentication,
  } = filters.filterMapping;
  const {
    filterCodeCitizenship,
    filterCodeCandidateType,
    filterCodeExamLevel,
    filterCodeSchool,
    filterCodeAuthentication,
  } = filters.filterCode;

  const { generateLoading, generateMutate } = generateAccount;
  const { emailLoading, emailMutate } = email;
  const { tableDispatch, state } = tableStateMgmt;
  return [
    candidateColumn(classes),
    statutoryNameColumn(remoteTotalRowsSelected, classes),
    citizenshipColumn(
      remoteTotalRowsSelected,
      classes,
      tableDispatch,
      filterMappingCitizenship,
      state,
      filterCodeCitizenship
    ),
    candidateTypeColumn(
      remoteTotalRowsSelected,
      classes,
      tableDispatch,
      filterMappingCandidateType,
      state,
      filterCodeCandidateType
    ),
    examLevelColumn(
      remoteTotalRowsSelected,
      classes,
      tableDispatch,
      filterMappingExamLevel,
      state,
      filterCodeExamLevel
    ),
    emailColumn(remoteTotalRowsSelected),
    schoolColumn(
      remoteTotalRowsSelected,
      classes,
      tableDispatch,
      filterMappingSchool,
      state,
      filterCodeSchool
    ),
    authenticationColumn(
      remoteTotalRowsSelected,
      classes,
      tableDispatch,
      filterMappingAuthentication,
      state,
      filterCodeAuthentication
    ),
    accountCreationColumn(
      generateLoading,
      classes,
      generateMutate,
      remoteTotalRowsSelected,
      hasCreateAccess
    ),
    emailSentColumn(classes, remoteTotalRowsSelected),
    actionColumn(classes, editDetail, hasEditAccess),
    sendEmailColumn(classes, emailLoading, emailMutate),
    {
      name: 'uuid',
      options: {
        display: false,
        filter: false,
      },
    },
    indexNumberColumn,
  ];
};

const candidateCreationSearchInputRequest = (state: TableState) => ({
  uuid: [],
  candidateCreationSearchParamModel: {
    candidateType: getIndexValue(state.filters.candidateType, 0),
    candidateSchool: state.filters.school || [],
    examLevel: getIndexValue(state.filters.examLevel, 0),
    authenticationType: getIndexValue(state.filters.authentication, 0),
    citizenship: getIndexValue(state.filters.citizenship, 0),
    search: state.search ?? '',
  },
});

interface UseDataTableForDataTable {
  state: TableState;
  tableDispatch: React.Dispatch<TableActionTypes>;
  tOptions: MUIDataTableOptions;
  selectionToolbar: (
    bulkActions: React.ReactNode[] | undefined,
    onClearSelection?: (() => void) | undefined
  ) => JSX.Element;
  remoteTotalRowsSelected: number;
}

interface UseCandidateCreateApiDataForDataTable {
  _refetch: () => void;
  isLoading: boolean;
  generateMutation: CustomMutateReturn<
    AxiosResponse<undefined>,
    AxiosError<GenericErrorResponse>,
    GenerateCandidateRequest[],
    unknown
  >;
  disabledEmailButton: boolean;
  callCheckEmailMutate: (
    type: 'ALL' | 'SELECTED' | 'INDIVIDUAL',
    inputRequest: RawCandidateFunctionRequest
  ) => void;
  fileUUID: string;
  onHandleExport: (inputRequest: RawCandidateFunctionRequest) => void;
  downloadCSV: (downloadResponse: RetrieveS3URLResponse) => void;
  disableExportButton: boolean;
}

interface IExamCandidateCreateDataTableProps {
  setEditDetail: React.Dispatch<any[]>;
  setOpen: React.Dispatch<boolean>;
  useDataTableForDataTable: UseDataTableForDataTable;
  useCandidateCreateApiDataForDataTable: UseCandidateCreateApiDataForDataTable;
  hasCreateAccess: boolean;
  hasEditAccess: boolean;
}

export const IExamCandidateCreateDataTable = ({
  setEditDetail,
  setOpen,
  useDataTableForDataTable,
  useCandidateCreateApiDataForDataTable,
  hasCreateAccess,
  hasEditAccess,
}: IExamCandidateCreateDataTableProps) => {
  const { t } = useTranslation();
  const [addedNAExamLevel, setAddedNAExamLevel] = useState(false);
  const {
    state,
    tableDispatch,
    tOptions,
    selectionToolbar,
    remoteTotalRowsSelected,
  } = useDataTableForDataTable;
  const {
    _refetch,
    isLoading,
    generateMutation,
    disabledEmailButton: emailLoading,
    callCheckEmailMutate,
    fileUUID,
    onHandleExport,
    downloadCSV,
    disableExportButton,
  } = useCandidateCreateApiDataForDataTable;

  const {
    candidateType: {
      functionCode: filterCodeCandidateType,
      typeMapping: filterMappingCandidateType,
    },
    school: { functionCode: filterCodeSchool, typeMapping: filterMappingSchool },
    examLevel: { functionCode: filterCodeExamLevel, typeMapping: filterMappingExamLevel },
    authentication: {
      functionCode: filterCodeAuthentication,
      typeMapping: filterMappingAuthentication,
    },
    citizenship: { functionCode: filterCodeCitizenship, typeMapping: filterMappingCitizenship },
  } = useRawFilters();

  if (filterCodeExamLevel.length > 0 && !addedNAExamLevel) {
    filterCodeExamLevel.push({ children: 'N/A', key: 'N/A', value: 'N/A' });
    setAddedNAExamLevel(true);
  }

  // Reset the flag if filter options is reset
  if (filterCodeExamLevel.length <= 0 && addedNAExamLevel) {
    setAddedNAExamLevel(false);
  }

  const { mutate: generateMutate, isLoading: generateLoading } = generateMutation;

  useEffect(() => {
    _refetch();
  }, [
    _refetch,
    state.filters,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
    state.search,
  ]);

  const exportEmailAllAction = useCallback(
    () => (
      <>
        <Button
          variant="text"
          color="primary"
          disabled={emailLoading}
          onClick={() => callCheckEmailMutate('ALL', candidateCreationSearchInputRequest(state))}
        >
          {t('admin->>IExamCandidateCreate.Email All')}
        </Button>
        <DownloadFromS3Action
          color="primary"
          actionType="link"
          requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
          disable={disableExportButton}
          onClick={() => {
            onHandleExport(candidateCreationSearchInputRequest(state));
          }}
          onDownload={(status) => downloadCSV(status)}
          label={t('admin->>IExamCandidateCreate.Export All')}
        />
      </>
    ),
    [
      callCheckEmailMutate,
      disableExportButton,
      downloadCSV,
      fileUUID,
      onHandleExport,
      emailLoading,
      state,
      t,
    ]
  );

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      selectableRows: 'multiple',
      pagination: true,
      filter: true,
      sort: true,
      filterType: 'multiselect',
      selectToolbarPlacement: 'replace',
      customToolbar: exportEmailAllAction,
    }),
    [tOptions, exportEmailAllAction]
  );

  const [localAuthentication, setLocalAuthentication] = useState<{ [key: string]: any }>({});
  const saveLocalAuthentication = useCallback(
    (event: any, tmpMeta: MUIDataTableMeta) => {
      setLocalAuthentication({
        ...localAuthentication,
        [tmpMeta.rowData[0]]: event.target.value,
      });
    },
    [localAuthentication]
  );

  const handleOpen = (detail: any[]) => {
    setEditDetail(detail);
    setOpen(true);
  };

  const tableColumns = useTableColumns({
    remoteTotalRowsSelected,
    hasCreateAccess,
    hasEditAccess,
    tableStateMgmt: {
      state,
      tableDispatch,
    },
    filters: {
      filterMapping: {
        filterMappingCitizenship,
        filterMappingCandidateType,
        filterMappingExamLevel,
        filterMappingSchool,
        filterMappingAuthentication,
      },
      filterCode: {
        filterCodeCitizenship,
        filterCodeCandidateType,
        filterCodeExamLevel,
        filterCodeSchool,
        filterCodeAuthentication,
      },
    },
    generateAccount: {
      generateLoading,
      generateMutate: (tableMeta: MUIDataTableMeta) =>
        generateMutate([
          {
            uuid: tableMeta.rowData[12],
            authentication: localAuthentication[tableMeta.rowData[0]] || tableMeta.rowData[7],
          },
        ]),
    },
    email: {
      emailLoading,
      emailMutate: (tableMeta: MUIDataTableMeta) =>
        callCheckEmailMutate('INDIVIDUAL', {
          uuid: [tableMeta.rowData[12]],
          candidateCreationSearchParamModel: candidateCreationSearchInputRequest(state)
            .candidateCreationSearchParamModel,
        }),
    },
    saveLocalAuthentication,
    editDetail: handleOpen,
  });

  const bulkActionComponent = useBulkActions(
    state,
    localAuthentication,
    generateMutation,
    callCheckEmailMutate,
    emailLoading,
    {
      onHandleExport,
      fileUUID,
      downloadCSV,
      disableExportButton,
    },
    {
      emailSelectedLabel: t('admin->>IExamCandidateCreate.Email Selected'),
      exportSelectedLabel: t('admin->>IExamCandidateCreate.Export Selected'),
    }
  );

  return (
    <MUIDataTable
      isLoading={isLoading}
      state={state}
      dispatch={tableDispatch}
      columns={tableColumns}
      data={state.data}
      options={customOptions}
      rowsPerPageOptions={[5, 10, 20, 50]}
      components={{ TableToolbarSelect: () => selectionToolbar(bulkActionComponent) }}
    />
  );
};
